import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ConfirmEmail from './pages/ConfirmEmail';
import Profile from './pages/Profile';
import HRM from './pages/HRM';
import AddGym from './pages/AddGym';
import GymDetail from './pages/GymDetail';
import GymEdit from './pages/GymEdit';
import RouteDetail from './pages/RouteDetail';
import ClimbDetail from './pages/ClimbDetail';
import AddClimbingRoute from './pages/AddClimbingRoute';
import EditClimbingRoute from './pages/EditClimbingRoute';
import Dashboard from './pages/admin/Dashboard';
import ClimberDetail from './pages/admin/ClimberDetail';
import LogClimbPage from './pages/LogClimbPage';
import { MyGymsList, MyGymDetail } from './pages/MyGyms';
import About from './pages/About';
import JoinGym from './pages/JoinGym';
import ProtectedRoute from './components/ProtectedRoute';
import HomeRedirect from './components/HomeRedirect';
import UpdatePrompt from './components/UpdatePrompt';
import AuthActionHandler from './components/AuthActionHandler';
import RootAuthRedirect from './components/RootAuthRedirect';
import { AuthProvider } from './contexts/AuthContext';
import { useAutoUpdateCheck } from './hooks/useAutoUpdateCheck';
import './styles/App.css';

function App() {
  // Global viewport height handler
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Initial setup
    setVH();
    
    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

function AppContent() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  
  // Check if app is running as PWA
  useEffect(() => {
    const checkPWA = () => {
      const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone || 
                        document.referrer.includes('android-app://');
      setIsPWA(isPWAMode);
    };
    
    checkPWA();
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkPWA);
    
    return () => {
      mediaQuery.removeEventListener('change', checkPWA);
    };
  }, []);
  
  const {
    updateAvailable,
    applyUpdate,
    dismissUpdate
  } = useAutoUpdateCheck({
    checkOnMount: isPWA, // Only auto-check for PWA
    checkInterval: isPWA ? 30 * 60 * 1000 : 0, // Check every 30 minutes for PWA only
  });

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await applyUpdate();
    } catch (error) {
      console.error('Update failed:', error);
      setIsUpdating(false);
    }
  };

  return (
    <div className="App">
      <Routes>

            
            
            {/* Protected routes */}
            <Route 
              path="/" 
              element={<RootAuthRedirect />} 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            {/* Redirect old my-gym route to new my-gyms */}
            <Route 
              path="/my-gym" 
              element={<Navigate to="/my-gyms" replace />}
            />
            
            {/* Gyms routes - redirect to appropriate gym or join page */}
            <Route 
              path="/gyms" 
              element={
                <ProtectedRoute>
                  <HomeRedirect />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/add" 
              element={
                <ProtectedRoute>
                  <AddGym />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId" 
              element={
                <ProtectedRoute>
                  <GymDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId/edit" 
              element={
                <ProtectedRoute>
                  <GymEdit />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId/routes/add" 
              element={
                <ProtectedRoute>
                  <AddClimbingRoute />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId/routes/:routeId" 
              element={
                <ProtectedRoute>
                  <RouteDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId/routes/:routeId/log" 
              element={
                <ProtectedRoute>
                  <LogClimbPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId/climbs/:climbIndex" 
              element={
                <ProtectedRoute>
                  <ClimbDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/gyms/:gymId/routes/:routeId/edit" 
              element={
                <ProtectedRoute>
                  <EditClimbingRoute />
                </ProtectedRoute>
              } 
            />
            
            {/* Climbing routes - redirect to gyms */}
            <Route 
              path="/climbing/my-climbs" 
              element={<Navigate to="/gyms" replace />}
            />
            <Route 
              path="/climbing/my-climbs/:climbId" 
              element={<Navigate to="/gyms" replace />}
            />
            
            {/* Admin routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/climber/:climberId" 
              element={
                <ProtectedRoute>
                  <ClimberDetail />
                </ProtectedRoute>
              } 
            />
            
            {/* My Gyms routes */}
            <Route 
              path="/my-gyms" 
              element={
                <ProtectedRoute>
                  <MyGymsList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-gyms/:gymId/edit" 
              element={
                <ProtectedRoute>
                  <MyGymDetail />
                </ProtectedRoute>
              } 
            />
            
            {/* About page */}
            <Route 
              path="/about" 
              element={
                <ProtectedRoute>
                  <About />
                </ProtectedRoute>
              } 
            />
            
            {/* Join Gym page */}
            <Route 
              path="/join-gym" 
              element={
                <ProtectedRoute>
                  <JoinGym />
                </ProtectedRoute>
              } 
            />
            
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/hrm" element={<HRM />} />
            
            {/* Firebase auth action handler route */}
            <Route path="/auth/action" element={<AuthActionHandler />} />
            
            {/* Redirect any unknown routes to login */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
          
          {/* Update prompt overlay - only show for PWA */}
          {isPWA && (
            <UpdatePrompt
              show={updateAvailable}
              onUpdate={handleUpdate}
              onDismiss={dismissUpdate}
              isUpdating={isUpdating}
            />
          )}
        </div>
  );
}

export default App;